import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../services/supabase';
import { useLovense } from '../../hooks/useLovense';
import { useSessionStore } from '../../hooks/useSession';
import { SafeWordButton } from '../SafeWordButton';
import toast from 'react-hot-toast';

export const TeaseDenial = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const role = (searchParams.get('role') || 'A') as 'A' | 'B';
  
  const { vibrate, setPattern, stop } = useLovense();
  const { session } = useSessionStore();
  
  const [intensity, setIntensity] = useState(0);
  const [partnerIntensity, setPartnerIntensity] = useState(0);
  const [denialMode, setDenialMode] = useState(false);
  const [denialTimer, setDenialTimer] = useState(0);
  const [isController, setIsController] = useState(role === 'A');
  const channelRef = useRef<any>(null);

  useEffect(() => {
    const channel = supabase.channel(`game-${sessionId}`);
    channelRef.current = channel;
    
    channel
      .on('broadcast', { event: 'game_action' }, ({ payload }: any) => {
        const { action, response } = payload;
        if (action.playerId !== role) {
          if (action.action === 'intensity_increase') {
            setIntensity(response.intensity);
            vibrate(response.intensity);
          }
          if (action.action === 'denial_start') {
            setDenialMode(true);
            setDenialTimer(response.duration * 60);
            setPattern('Wave');
          }
          if (action.action === 'denial_stop') {
            setDenialMode(false);
            stop();
          }
          if (action.action === 'denial_edge') {
            vibrate(session?.consent.maxIntensity || 20);
            setTimeout(() => vibrate(2), 3000);
          }
        } else {
          if (action.action === 'intensity_increase') {
            setPartnerIntensity(response.intensity);
          }
        }
      })
      .subscribe();

    // Denial countdown
    const interval = setInterval(() => {
      setDenialTimer(prev => {
        if (prev > 0) return prev - 1;
        return 0;
      });
    }, 1000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [sessionId, role, vibrate, setPattern, stop, session]);

  const increaseIntensity = useCallback(() => {
    const newIntensity = Math.min((intensity || 0) + 2, session?.consent.maxIntensity || 20);
    channelRef.current?.send({
      type: 'broadcast',
      event: 'game_action',
      payload: { action: { playerId: role, action: 'intensity_increase' }, response: { intensity: newIntensity } }
    });
  }, [intensity, session, role]);

  const decreaseIntensity = useCallback(() => {
    const newIntensity = Math.max((intensity || 0) - 2, 0);
    channelRef.current?.send({
      type: 'broadcast',
      event: 'game_action',
      payload: { action: { playerId: role, action: 'intensity_increase' }, response: { intensity: newIntensity } }
    });
  }, [intensity, role]);

  const startDenial = useCallback(() => {
    const duration = session?.consent.maxDenialDuration || 5;
    channelRef.current?.send({
      type: 'broadcast',
      event: 'game_action',
      payload: { action: { playerId: role, action: 'denial_start' }, response: { duration } }
    });
    toast.success(`Denial mode started for ${duration} minutes`);
  }, [session, role]);

  const edge = useCallback(() => {
    channelRef.current?.send({
      type: 'broadcast',
      event: 'game_action',
      payload: { action: { playerId: role, action: 'denial_edge' }, response: {} }
    });
  }, [role]);

  const stopDenial = useCallback(() => {
    channelRef.current?.send({
      type: 'broadcast',
      event: 'game_action',
      payload: { action: { playerId: role, action: 'denial_stop' }, response: {} }
    });
    stop();
  }, [role, stop]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="game-container">
      <div style={{ textAlign: 'center' }}>
        <h1>Tease & Denial</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          {isController ? 'Control your partner\'s pleasure' : 'Your partner controls your pleasure'}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', width: '100%', maxWidth: '600px' }}>
        {/* Your status */}
        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ marginBottom: '1rem', color: 'var(--primary)' }}>Your Toy</h3>
          <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            {intensity}
          </div>
          <div style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>Intensity</div>
          
          {denialMode && (
            <div style={{ 
              background: 'var(--secondary)', 
              padding: '0.5rem 1rem', 
              borderRadius: '20px',
              marginBottom: '1rem'
            }}>
              ⏱️ {formatTime(denialTimer)}
            </div>
          )}
          
          <div style={{ height: '200px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '4px' }}>
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: '8px',
                  height: `${(i + 1) * 10}px`,
                  background: i < intensity ? 'var(--primary)' : 'var(--bg-tertiary)',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        </div>

        {/* Partner status */}
        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ marginBottom: '1rem', color: 'var(--secondary)' }}>Partner's Toy</h3>
          <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            {partnerIntensity}
          </div>
          <div style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>Intensity</div>
          
          <div style={{ height: '200px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '4px' }}>
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: '8px',
                  height: `${(i + 1) * 10}px`,
                  background: i < partnerIntensity ? 'var(--secondary)' : 'var(--bg-tertiary)',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card" style={{ width: '100%', maxWidth: '600px' }}>
        <h3 style={{ marginBottom: '1rem', textAlign: 'center' }}>Controls</h3>
        
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <button className="btn btn-secondary btn-lg" onClick={decreaseIntensity}>
            ⬇️ Decrease
          </button>
          <button className="btn btn-primary btn-lg" onClick={increaseIntensity}>
            ⬆️ Increase
          </button>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => setPattern('Pulse')}>
            Pulse
          </button>
          <button className="btn btn-secondary" onClick={() => setPattern('Wave')}>
            Wave
          </button>
          <button className="btn btn-secondary" onClick={() => setPattern('Fireworks')}>
            Fireworks
          </button>
          <button className="btn btn-danger" onClick={stop}>
            Stop
          </button>
        </div>

        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          {!denialMode ? (
            <button className="btn btn-primary btn-lg" onClick={startDenial}>
              🏁 Start Denial Mode
            </button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={edge}>
                😰 Bring to Edge
              </button>
              <button className="btn btn-danger btn-lg" onClick={stopDenial}>
                ✅ End Denial
              </button>
            </>
          )}
        </div>
      </div>

      <SafeWordButton sessionId={sessionId!} />
    </div>
  );
};